import * as React from 'react'

import { Box, Icon, SearchField, Spinner } from '@factorialco/deprecated-design-system'
import {
  BookkeeperCustomerFragment,
  BookkeeperEmployeeFragment,
  BookkeeperEmployeesDocument,
  BookkeeperFragment,
  BookkeepersDocument,
} from 'generated/resources/graphql'

import FactorialTip from 'modules/core/components/FactorialTip'
import NotFound from 'modules/core/components/NotFound'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import CompanyBookkeeper from './CompanyBookkeeper'
import NoAccessWarning from './NoAccessWarning'

interface Props {
  bookkeeperCompanyId: number
  selectedCustomer: BookkeeperCustomerFragment
  setSelectedCustomer: (customer: BookkeeperCustomerFragment | null) => void
}

interface PanelProps {
  title: string
  onClose: (customer: BookkeeperCustomerFragment | null) => void
}

const Panel = ({ title, onClose, children }: React.PropsWithChildren<PanelProps>) => {
  return (
    <Sidepanel
      overlay
      boxPadding
      width="wide"
      title={title}
      onClose={() => onClose(null)}
      open
      headerBorder
      closeLabel={i18n.t('common.close')}
    >
      {children}
    </Sidepanel>
  )
}

const CompanyBookkeepers = ({
  bookkeeperCompanyId,
  selectedCustomer,
  setSelectedCustomer,
}: Props) => {
  const { navigation } = useNavigation()
  const { access } = useCurrentSession()
  const currentAccessId = access.id
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const bookkeeperContext = {
    isBookkeeperView: true,
  }
  const { data: bookkeepersData } = useQuery(BookkeepersDocument, {
    context: bookkeeperContext,
    variables: {
      companyIds: [selectedCustomer.customerCompanyId],
    },
  })
  const { data: bookkeeperEmployeesData } = useQuery(BookkeeperEmployeesDocument, {
    context: bookkeeperContext,
    variables: {
      companyId: bookkeeperCompanyId,
      fullName: debouncedSearch,
    },
  })
  const companyName = selectedCustomer.customerCompanyName

  if (!bookkeepersData || !bookkeeperEmployeesData) {
    return (
      <Panel title={`${companyName} bookkeepers`} onClose={() => setSelectedCustomer(null)}>
        <Box width="full" height="full" alignItems="center" justifyContent="center">
          <Spinner />
        </Box>
      </Panel>
    )
  }

  const bookkeepers: BookkeeperFragment[] =
    bookkeepersData.bookkeepersManagement.bookkeepersConnection.nodes
  const availableBookkeepers: BookkeeperEmployeeFragment[] =
    bookkeeperEmployeesData.employees.employeesConnection.nodes
  const currentBookkeeperEmployee = availableBookkeepers.find(
    (bk) => bk.access?.id === currentAccessId
  )
  if (!currentBookkeeperEmployee && !debouncedSearch) return <NotFound />
  const hasbookkeeperAccess =
    !!debouncedSearch ||
    bookkeepers.some((bk) => bk.access?.userId === currentBookkeeperEmployee?.access?.userId)

  return (
    <Panel
      title={`${selectedCustomer.customerCompanyName} bookkeepers`}
      onClose={() => setSelectedCustomer(null)}
    >
      <Box>
        <Box gap="s24">
          {hasbookkeeperAccess ? (
            <>
              <Box>
                <SearchField
                  variant="big"
                  placeholder={i18n.t('bookkeepers.companies.toggle_bookkeeper.search_placeholder')}
                  search={search}
                  onSearch={onSearch}
                  autofocus
                />
              </Box>
              <Box gap="s12">
                {availableBookkeepers.map((availableBookkeeper) => {
                  if (!availableBookkeeper.access) return null

                  return (
                    <CompanyBookkeeper
                      key={`bookkeeper_${availableBookkeeper.id}`}
                      bookkeeperName={availableBookkeeper.fullName}
                      bookkeeperEmployeeAccessId={availableBookkeeper.access.id}
                      customerCompanyId={selectedCustomer.customerCompanyId}
                      bookkeeper={bookkeepers.find(
                        (bk) => bk.access?.userId === availableBookkeeper.access?.userId
                      )}
                    />
                  )
                })}
              </Box>
              <FactorialTip
                icon="lightbulb"
                routerTo={navigation.employees.fullPath()}
                routerToText={i18n.t('bookkeepers.companies.bookkeeper.more_bookkeepers_tip.link')}
                message={i18n.t('bookkeepers.companies.bookkeeper.more_bookkeepers_tip.message', {
                  name: companyName,
                })}
                title={i18n.t('bookkeepers.companies.bookkeeper.more_bookkeepers_tip.title')}
              />
            </>
          ) : (
            <>
              {currentBookkeeperEmployee?.access && (
                <Box gap="s12" flexDirection="row" alignItems="center" justifyContent="flexStart">
                  <CompanyBookkeeper
                    key={`bookkeeper_${currentBookkeeperEmployee.id}`}
                    bookkeeperName={currentBookkeeperEmployee.fullName}
                    bookkeeperEmployeeAccessId={currentBookkeeperEmployee.access.id}
                    customerCompanyId={selectedCustomer.customerCompanyId}
                    bookkeeper={undefined}
                  />
                  <Icon.Medium icon="info-circle-line" color="tangerine1000" />
                </Box>
              )}
              <NoAccessWarning />
            </>
          )}
        </Box>
      </Box>
    </Panel>
  )
}

export default CompanyBookkeepers
